import { parseIonicSpecies } from './ionicEquation';

export type ElectrochemMode = 'half' | 'galvanic' | 'electrolysis' | 'batteries';
export type EquationSide = 'left' | 'right';
export type RedoxProcess = 'oxidation' | 'reduction';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const ELECTROCHEM_TOOL_IDENTITY = {
  id: 'electrochemical-cells',
  toolKey: 'chemistry-electrochemical-cells',
  toolVersion: '1.1.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const ELECTROCHEM_MODES: ElectrochemMode[] = ['half', 'galvanic', 'electrolysis', 'batteries'];

/**
 * Year-to-mode table for CT-MODE. Every supported Year starts on half-equations; galvanic cells,
 * electrolysis and batteries stay learner-selectable because each builds on balanced half-equations.
 */
export const ELECTROCHEM_YEAR_MODES: readonly { mode: ElectrochemMode; fromYear: number }[] = [
  { mode: 'half', fromYear: 11 },
];

export const REVIEW_STATUS = 'pending-teaching-review' as const;

export interface DataSource {
  id: string;
  citation: string;
}

export const ELECTROCHEM_SOURCES: Record<'crc' | 'codata' | 'iupacWeights' | 'textbook', DataSource> =
  {
    crc: {
      id: 'crc',
      citation:
        'Vanýsek, P. “Electrochemical Series”, CRC Handbook of Chemistry and Physics (Rumble, ed.), standard reduction potentials at 298.15 K.',
    },
    codata: {
      id: 'codata',
      citation:
        'NIST CODATA 2018 recommended values: Faraday constant F = 96 485.332 12 C mol⁻¹ (rounded here to 96 485 C mol⁻¹).',
    },
    iupacWeights: {
      id: 'iupacWeights',
      citation:
        'IUPAC Commission on Isotopic Abundances and Atomic Weights, abridged standard atomic weights (2021).',
    },
    textbook: {
      id: 'textbook',
      citation:
        'Standard senior chemistry treatment of electrolysis products (molten salts, inert-electrode aqueous electrolysis, Hall–Héroult cell, chlor-alkali exception).',
    },
  };

export const STANDARD_CONDITIONS = {
  temperatureC: 25,
  temperatureK: 298.15,
  concentrationMolL: 1,
  gasPressureKPa: 100,
  label: '25 °C (298 K), 1 mol L⁻¹ for dissolved species, 100 kPa for gases',
} as const;

/** Faraday constant in C mol⁻¹ (NIST CODATA 2018, rounded to 5 s.f. as on senior data sheets). */
export const FARADAY_CONSTANT = 96485;
export const SECONDS_PER_MINUTE = 60;
export const MAX_HELPER_COUNT = 20;
export const MAX_MAIN_COEFFICIENT = 6;

/* ------------------------------------------------------------------ species */

interface SpeciesSeed {
  ascii: string;
  display: string;
}

export interface SpeciesDef {
  id: string;
  display: string;
  ascii: string;
  elements: Record<string, number>;
  charge: number;
}

const SPECIES_SEEDS = {
  F2: { ascii: 'F2(g)', display: 'F₂(g)' },
  F: { ascii: 'F-(aq)', display: 'F⁻(aq)' },
  Cl2: { ascii: 'Cl2(g)', display: 'Cl₂(g)' },
  Cl: { ascii: 'Cl-(aq)', display: 'Cl⁻(aq)' },
  Br2: { ascii: 'Br2(l)', display: 'Br₂(l)' },
  Br: { ascii: 'Br-(aq)', display: 'Br⁻(aq)' },
  I2: { ascii: 'I2(s)', display: 'I₂(s)' },
  I: { ascii: 'I-(aq)', display: 'I⁻(aq)' },
  O2: { ascii: 'O2(g)', display: 'O₂(g)' },
  H: { ascii: 'H+(aq)', display: 'H⁺(aq)' },
  H2: { ascii: 'H2(g)', display: 'H₂(g)' },
  H2O: { ascii: 'H2O(l)', display: 'H₂O(l)' },
  OH: { ascii: 'OH-(aq)', display: 'OH⁻(aq)' },
  Ag: { ascii: 'Ag(s)', display: 'Ag(s)' },
  Agp: { ascii: 'Ag+(aq)', display: 'Ag⁺(aq)' },
  Fe3: { ascii: 'Fe^3+(aq)', display: 'Fe³⁺(aq)' },
  Fe2: { ascii: 'Fe^2+(aq)', display: 'Fe²⁺(aq)' },
  Fe: { ascii: 'Fe(s)', display: 'Fe(s)' },
  Cu2: { ascii: 'Cu^2+(aq)', display: 'Cu²⁺(aq)' },
  Cu: { ascii: 'Cu(s)', display: 'Cu(s)' },
  Pb2: { ascii: 'Pb^2+(aq)', display: 'Pb²⁺(aq)' },
  Pb: { ascii: 'Pb(s)', display: 'Pb(s)' },
  Sn2: { ascii: 'Sn^2+(aq)', display: 'Sn²⁺(aq)' },
  Sn: { ascii: 'Sn(s)', display: 'Sn(s)' },
  Ni2: { ascii: 'Ni^2+(aq)', display: 'Ni²⁺(aq)' },
  Ni: { ascii: 'Ni(s)', display: 'Ni(s)' },
  Zn2: { ascii: 'Zn^2+(aq)', display: 'Zn²⁺(aq)' },
  Zn: { ascii: 'Zn(s)', display: 'Zn(s)' },
  Al3: { ascii: 'Al^3+(aq)', display: 'Al³⁺(aq)' },
  Al: { ascii: 'Al(s)', display: 'Al(s)' },
  Mg2: { ascii: 'Mg^2+(aq)', display: 'Mg²⁺(aq)' },
  Mg: { ascii: 'Mg(s)', display: 'Mg(s)' },
  Kp: { ascii: 'K+(aq)', display: 'K⁺(aq)' },
  K: { ascii: 'K(s)', display: 'K(s)' },
  MnO4: { ascii: 'MnO4-(aq)', display: 'MnO₄⁻(aq)' },
  Mn2: { ascii: 'Mn^2+(aq)', display: 'Mn²⁺(aq)' },
  Cr2O7: { ascii: 'Cr2O7^2-(aq)', display: 'Cr₂O₇²⁻(aq)' },
  Cr3: { ascii: 'Cr^3+(aq)', display: 'Cr³⁺(aq)' },
  NO3: { ascii: 'NO3-(aq)', display: 'NO₃⁻(aq)' },
  NO: { ascii: 'NO(g)', display: 'NO(g)' },
  SO2: { ascii: 'SO2(aq)', display: 'SO₂(aq)' },
  SO4: { ascii: 'SO4^2-(aq)', display: 'SO₄²⁻(aq)' },
  // Molten-salt species (state (l) for the melt).
  NaMelt: { ascii: 'Na+(l)', display: 'Na⁺(l)' },
  NaLiq: { ascii: 'Na(l)', display: 'Na(l)' },
  ClMelt: { ascii: 'Cl-(l)', display: 'Cl⁻(l)' },
  Pb2Melt: { ascii: 'Pb^2+(l)', display: 'Pb²⁺(l)' },
  PbLiq: { ascii: 'Pb(l)', display: 'Pb(l)' },
  BrMelt: { ascii: 'Br-(l)', display: 'Br⁻(l)' },
  Br2Gas: { ascii: 'Br2(g)', display: 'Br₂(g)' },
  Al3Melt: { ascii: 'Al^3+(l)', display: 'Al³⁺(melt)' },
  AlLiq: { ascii: 'Al(l)', display: 'Al(l)' },
  OxideMelt: { ascii: 'O^2-(l)', display: 'O²⁻(melt)' },
  Mg2Melt: { ascii: 'Mg^2+(l)', display: 'Mg²⁺(l)' },
  MgLiq: { ascii: 'Mg(l)', display: 'Mg(l)' },
} satisfies Record<string, SpeciesSeed>;

export type SpeciesId = keyof typeof SPECIES_SEEDS;

function buildSpecies(id: SpeciesId, seed: SpeciesSeed): SpeciesDef {
  const parsed = parseIonicSpecies(seed.ascii);
  if (!parsed.species) {
    throw new Error(`Electrochemistry species ${id} failed to parse: ${seed.ascii}`);
  }
  return {
    id,
    display: seed.display,
    ascii: seed.ascii,
    elements: { ...parsed.species.atoms },
    charge: parsed.species.charge,
  };
}

export const SPECIES = Object.fromEntries(
  (Object.entries(SPECIES_SEEDS) as [SpeciesId, SpeciesSeed][]).map(([id, seed]) => [
    id,
    buildSpecies(id, seed),
  ]),
) as Record<SpeciesId, SpeciesDef>;

export const ELECTRON_DISPLAY = 'e⁻';

/* --------------------------------------------------------- equations core */

export interface Term {
  species: SpeciesId;
  coefficient: number;
}

export interface HalfEquation {
  left: Term[];
  right: Term[];
  electrons: number;
  electronSide: EquationSide;
}

export interface FullEquation {
  left: Term[];
  right: Term[];
}

/* ------------------------------------------------------------ half mode */

export type HalfMedium = 'simple' | 'acidic';

export interface HalfEquationCase {
  id: string;
  title: string;
  context: string;
  medium: HalfMedium;
  process: RedoxProcess;
  main: { left: SpeciesId; right: SpeciesId };
  /** The element whose oxidation state changes. */
  changingElement: string;
  reference: HalfEquation;
}

export const term = (species: SpeciesId, coefficient = 1): Term => ({ species, coefficient });

export const HALF_EQUATION_CASES: HalfEquationCase[] = [
  {
    id: 'zinc-oxidation',
    title: 'Zinc metal dissolves',
    context: 'A zinc strip slowly dissolves in a solution as Zn²⁺ ions form.',
    medium: 'simple',
    process: 'oxidation',
    main: { left: 'Zn', right: 'Zn2' },
    changingElement: 'Zn',
    reference: { left: [term('Zn')], right: [term('Zn2')], electrons: 2, electronSide: 'right' },
  },
  {
    id: 'copper-reduction',
    title: 'Copper plates out',
    context: 'Copper(II) ions in solution become a pink-brown copper coating.',
    medium: 'simple',
    process: 'reduction',
    main: { left: 'Cu2', right: 'Cu' },
    changingElement: 'Cu',
    reference: { left: [term('Cu2')], right: [term('Cu')], electrons: 2, electronSide: 'left' },
  },
  {
    id: 'silver-reduction',
    title: 'Silver crystals grow',
    context: 'Silver ions gain electrons and grow silver crystals on a wire.',
    medium: 'simple',
    process: 'reduction',
    main: { left: 'Agp', right: 'Ag' },
    changingElement: 'Ag',
    reference: { left: [term('Agp')], right: [term('Ag')], electrons: 1, electronSide: 'left' },
  },
  {
    id: 'iron-ii-oxidation',
    title: 'Iron(II) becomes iron(III)',
    context: 'A pale green Fe²⁺ solution turns yellow-brown as Fe³⁺ forms.',
    medium: 'simple',
    process: 'oxidation',
    main: { left: 'Fe2', right: 'Fe3' },
    changingElement: 'Fe',
    reference: { left: [term('Fe2')], right: [term('Fe3')], electrons: 1, electronSide: 'right' },
  },
  {
    id: 'bromide-oxidation',
    title: 'Bromide becomes bromine',
    context: 'Colourless bromide ions form orange-brown bromine.',
    medium: 'simple',
    process: 'oxidation',
    main: { left: 'Br', right: 'Br2' },
    changingElement: 'Br',
    reference: { left: [term('Br', 2)], right: [term('Br2')], electrons: 2, electronSide: 'right' },
  },
  {
    id: 'chlorine-reduction',
    title: 'Chlorine becomes chloride',
    context: 'Chlorine gas dissolves and is converted to chloride ions.',
    medium: 'simple',
    process: 'reduction',
    main: { left: 'Cl2', right: 'Cl' },
    changingElement: 'Cl',
    reference: { left: [term('Cl2')], right: [term('Cl', 2)], electrons: 2, electronSide: 'left' },
  },
  {
    id: 'aluminium-oxidation',
    title: 'Aluminium forms Al³⁺',
    context: 'Aluminium metal loses electrons to form aluminium ions.',
    medium: 'simple',
    process: 'oxidation',
    main: { left: 'Al', right: 'Al3' },
    changingElement: 'Al',
    reference: { left: [term('Al')], right: [term('Al3')], electrons: 3, electronSide: 'right' },
  },
  {
    id: 'permanganate-reduction',
    title: 'Permanganate in acid',
    context: 'Purple MnO₄⁻ becomes almost colourless Mn²⁺ in acidified solution.',
    medium: 'acidic',
    process: 'reduction',
    main: { left: 'MnO4', right: 'Mn2' },
    changingElement: 'Mn',
    reference: {
      left: [term('MnO4'), term('H', 8)],
      right: [term('Mn2'), term('H2O', 4)],
      electrons: 5,
      electronSide: 'left',
    },
  },
  {
    id: 'dichromate-reduction',
    title: 'Dichromate in acid',
    context: 'Orange Cr₂O₇²⁻ becomes green Cr³⁺ in acidified solution.',
    medium: 'acidic',
    process: 'reduction',
    main: { left: 'Cr2O7', right: 'Cr3' },
    changingElement: 'Cr',
    reference: {
      left: [term('Cr2O7'), term('H', 14)],
      right: [term('Cr3', 2), term('H2O', 7)],
      electrons: 6,
      electronSide: 'left',
    },
  },
  {
    id: 'nitrate-reduction',
    title: 'Nitrate to nitrogen monoxide',
    context: 'In acid, nitrate ions are reduced to colourless NO gas.',
    medium: 'acidic',
    process: 'reduction',
    main: { left: 'NO3', right: 'NO' },
    changingElement: 'N',
    reference: {
      left: [term('NO3'), term('H', 4)],
      right: [term('NO'), term('H2O', 2)],
      electrons: 3,
      electronSide: 'left',
    },
  },
  {
    id: 'sulfur-dioxide-oxidation',
    title: 'Sulfur dioxide to sulfate',
    context: 'Dissolved SO₂ is oxidised to sulfate ions, releasing H⁺.',
    medium: 'acidic',
    process: 'oxidation',
    main: { left: 'SO2', right: 'SO4' },
    changingElement: 'S',
    reference: {
      left: [term('SO2'), term('H2O', 2)],
      right: [term('SO4'), term('H', 4)],
      electrons: 2,
      electronSide: 'right',
    },
  },
];

/* -------------------------------------------------------- galvanic mode */

export type GalvanicUse = 'buildable' | 'ranking-only';

export interface ReductionCouple {
  id: string;
  label: string;
  reduction: HalfEquation;
  /** Value used in calculations, rounded to 0.01 V. */
  e0V: number;
  /** More precise CRC value, kept to show the rounding. */
  sourceValueV: number;
  electrodeLabel: string;
  electrodeKind: 'metal' | 'inert';
  electrolyteLabel: string;
  galvanicUse: GalvanicUse;
  note?: string;
  sourceId: 'crc';
}

const couple = (
  id: string,
  label: string,
  reduction: HalfEquation,
  e0V: number,
  sourceValueV: number,
  electrode: { label: string; kind: 'metal' | 'inert' },
  electrolyteLabel: string,
  galvanicUse: GalvanicUse = 'buildable',
  note?: string,
): ReductionCouple => ({
  id,
  label,
  reduction,
  e0V,
  sourceValueV,
  electrodeLabel: electrode.label,
  electrodeKind: electrode.kind,
  electrolyteLabel,
  galvanicUse,
  note,
  sourceId: 'crc',
});

const INERT = { label: 'Pt', kind: 'inert' as const };
const metal = (label: string) => ({ label, kind: 'metal' as const });
const reduce = (left: Term[], right: Term[], electrons: number): HalfEquation => ({
  left,
  right,
  electrons,
  electronSide: 'left',
});

/** Standard reduction potentials, highest (strongest oxidant) first. */
export const REDUCTION_POTENTIALS: ReductionCouple[] = [
  couple('fluorine', 'F₂/F⁻', reduce([term('F2')], [term('F', 2)], 2), 2.87, 2.866, INERT, 'F⁻(aq)', 'ranking-only', 'Fluorine reacts with water, so this half-cell is used for ranking only.'),
  couple('chlorine', 'Cl₂/Cl⁻', reduce([term('Cl2')], [term('Cl', 2)], 2), 1.36, 1.35827, INERT, 'Cl⁻(aq) with Cl₂ bubbled in'),
  couple('oxygen-acid', 'O₂, H⁺/H₂O', reduce([term('O2'), term('H', 4)], [term('H2O', 2)], 4), 1.23, 1.229, INERT, 'H⁺(aq) with O₂ bubbled in'),
  couple('silver', 'Ag⁺/Ag', reduce([term('Agp')], [term('Ag')], 1), 0.8, 0.7996, metal('Ag'), 'AgNO₃(aq)'),
  couple('iron-iii', 'Fe³⁺/Fe²⁺', reduce([term('Fe3')], [term('Fe2')], 1), 0.77, 0.771, INERT, 'Fe³⁺(aq) and Fe²⁺(aq)'),
  couple('iodine', 'I₂/I⁻', reduce([term('I2')], [term('I', 2)], 2), 0.54, 0.5355, INERT, 'I⁻(aq) with I₂'),
  couple('copper', 'Cu²⁺/Cu', reduce([term('Cu2')], [term('Cu')], 2), 0.34, 0.3419, metal('Cu'), 'CuSO₄(aq)'),
  couple('hydrogen', 'H⁺/H₂', reduce([term('H', 2)], [term('H2')], 2), 0, 0, INERT, 'H⁺(aq) with H₂ bubbled in', 'buildable', 'The standard hydrogen electrode is the 0.00 V reference by definition.'),
  couple('lead', 'Pb²⁺/Pb', reduce([term('Pb2')], [term('Pb')], 2), -0.13, -0.1262, metal('Pb'), 'Pb(NO₃)₂(aq)'),
  couple('tin', 'Sn²⁺/Sn', reduce([term('Sn2')], [term('Sn')], 2), -0.14, -0.1375, metal('Sn'), 'SnCl₂(aq)'),
  couple('nickel', 'Ni²⁺/Ni', reduce([term('Ni2')], [term('Ni')], 2), -0.26, -0.257, metal('Ni'), 'NiSO₄(aq)'),
  couple('iron-ii', 'Fe²⁺/Fe', reduce([term('Fe2')], [term('Fe')], 2), -0.45, -0.447, metal('Fe'), 'FeSO₄(aq)'),
  couple('zinc', 'Zn²⁺/Zn', reduce([term('Zn2')], [term('Zn')], 2), -0.76, -0.7618, metal('Zn'), 'ZnSO₄(aq)'),
  couple('aluminium', 'Al³⁺/Al', reduce([term('Al3')], [term('Al')], 3), -1.66, -1.662, metal('Al'), 'Al₂(SO₄)₃(aq)', 'buildable', 'A real aluminium electrode is coated in oxide, so measured voltages are lower than the standard value.'),
  couple('magnesium', 'Mg²⁺/Mg', reduce([term('Mg2')], [term('Mg')], 2), -2.37, -2.372, metal('Mg'), 'MgSO₄(aq)', 'buildable', 'Magnesium also reacts slowly with water, so a real cell drifts.'),
  couple('potassium', 'K⁺/K', reduce([term('Kp')], [term('K')], 1), -2.93, -2.931, metal('K'), 'K⁺(aq)', 'ranking-only', 'Potassium reacts violently with water, so this half-cell is used for ranking only.'),
];

export const SALT_BRIDGE = {
  electrolyte: 'KNO₃(aq)',
  cation: 'K⁺',
  anion: 'NO₃⁻',
} as const;

/* ---------------------------------------------------- electrolysis mode */

export type ElectrolyteKind = 'molten' | 'aqueous';

export interface ElectrodeProduct {
  id: string;
  label: string;
}

export interface ElectrolysisCase {
  id: string;
  name: string;
  kind: ElectrolyteKind;
  electrodes: string;
  cathode: { productId: string; half: HalfEquation; reason: string };
  anode: { productId: string; half: HalfEquation; reason: string };
  cathodeOptions: ElectrodeProduct[];
  anodeOptions: ElectrodeProduct[];
  /** Product counted by the Faraday calculation (formed at the cathode in every reviewed case). */
  faraday: { productLabel: string; molarMassGmol: number; electronsPerFormula: number };
  cations: string[];
  anions: string[];
  note?: string;
  sourceIds: ('textbook' | 'iupacWeights')[];
}

const P = {
  Na: { id: 'sodium', label: 'Sodium metal' },
  Pb: { id: 'lead', label: 'Lead metal' },
  Al: { id: 'aluminium', label: 'Aluminium metal' },
  Mg: { id: 'magnesium', label: 'Magnesium metal' },
  Cu: { id: 'copper', label: 'Copper metal' },
  Ag: { id: 'silver', label: 'Silver metal' },
  K: { id: 'potassium', label: 'Potassium metal' },
  H2: { id: 'hydrogen', label: 'Hydrogen gas' },
  Cl2: { id: 'chlorine', label: 'Chlorine gas' },
  Br2: { id: 'bromine', label: 'Bromine' },
  I2: { id: 'iodine', label: 'Iodine' },
  O2: { id: 'oxygen', label: 'Oxygen gas' },
  F2: { id: 'fluorine', label: 'Fluorine gas' },
  CuDissolves: { id: 'copper-dissolves', label: 'Copper anode dissolves to Cu²⁺' },
  S: { id: 'sulfur-species', label: 'Sulfate is oxidised' },
  NO3: { id: 'nitrate-species', label: 'Nitrate is oxidised' },
} as const;

/** Molar masses in g mol⁻¹ from IUPAC abridged standard atomic weights. */
export const MOLAR_MASS = {
  Na: 22.99,
  Pb: 207.2,
  Al: 26.98,
  Mg: 24.31,
  Cu: 63.55,
  Ag: 107.87,
  H2: 2.016,
} as const;

const OXYGEN_FROM_WATER = (): HalfEquation => ({
  left: [term('H2O', 2)],
  right: [term('O2'), term('H', 4)],
  electrons: 4,
  electronSide: 'right',
});

export const ELECTROLYSIS_CASES: ElectrolysisCase[] = [
  {
    id: 'molten-nacl',
    name: 'Molten sodium chloride',
    kind: 'molten',
    electrodes: 'Inert graphite electrodes',
    cathode: {
      productId: P.Na.id,
      half: reduce([term('NaMelt')], [term('NaLiq')], 1),
      reason: 'A molten salt has no water, so Na⁺ is the only species that can be reduced at the cathode.',
    },
    anode: {
      productId: P.Cl2.id,
      half: { left: [term('ClMelt', 2)], right: [term('Cl2')], electrons: 2, electronSide: 'right' },
      reason: 'Cl⁻ is the only anion in the melt, so it is oxidised to chlorine gas at the anode.',
    },
    cathodeOptions: [P.Na, P.H2],
    anodeOptions: [P.Cl2, P.O2],
    faraday: { productLabel: 'sodium', molarMassGmol: MOLAR_MASS.Na, electronsPerFormula: 1 },
    cations: ['Na⁺'],
    anions: ['Cl⁻'],
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'molten-pbbr2',
    name: 'Molten lead(II) bromide',
    kind: 'molten',
    electrodes: 'Inert graphite electrodes',
    cathode: {
      productId: P.Pb.id,
      half: reduce([term('Pb2Melt')], [term('PbLiq')], 2),
      reason: 'With no water present, Pb²⁺ ions are reduced to molten lead at the cathode.',
    },
    anode: {
      productId: P.Br2.id,
      half: { left: [term('BrMelt', 2)], right: [term('Br2Gas')], electrons: 2, electronSide: 'right' },
      reason: 'Br⁻ is the only anion, so it is oxidised to brown bromine vapour at the anode.',
    },
    cathodeOptions: [P.Pb, P.H2],
    anodeOptions: [P.Br2, P.O2],
    faraday: { productLabel: 'lead', molarMassGmol: MOLAR_MASS.Pb, electronsPerFormula: 2 },
    cations: ['Pb²⁺'],
    anions: ['Br⁻'],
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'molten-alumina',
    name: 'Aluminium oxide in molten cryolite',
    kind: 'molten',
    electrodes: 'Carbon cathode lining and carbon anodes',
    cathode: {
      productId: P.Al.id,
      half: reduce([term('Al3Melt')], [term('AlLiq')], 3),
      reason: 'Al³⁺ ions in the melt are reduced to liquid aluminium, which collects at the bottom of the cell.',
    },
    anode: {
      productId: P.O2.id,
      half: { left: [term('OxideMelt', 2)], right: [term('O2')], electrons: 4, electronSide: 'right' },
      reason: 'Oxide ions are oxidised to oxygen at the anode. Fluoride from the cryolite is far harder to oxidise, so fluorine does not form.',
    },
    cathodeOptions: [P.Al, P.Na],
    anodeOptions: [P.O2, P.F2],
    faraday: { productLabel: 'aluminium', molarMassGmol: MOLAR_MASS.Al, electronsPerFormula: 3 },
    cations: ['Al³⁺', 'Na⁺'],
    anions: ['O²⁻', 'AlF₆³⁻'],
    note: 'Al₂O₃ melts above 2000 °C, so it is dissolved in molten cryolite (Na₃AlF₆) at about 950 °C. The hot oxygen reacts with the carbon anodes to form CO₂, so the anodes are replaced regularly (Hall–Héroult process).',
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'molten-mgcl2',
    name: 'Molten magnesium chloride',
    kind: 'molten',
    electrodes: 'Inert electrodes',
    cathode: {
      productId: P.Mg.id,
      half: reduce([term('Mg2Melt')], [term('MgLiq')], 2),
      reason: 'In the melt, Mg²⁺ is the only cation, so it is reduced to magnesium at the cathode.',
    },
    anode: {
      productId: P.Cl2.id,
      half: { left: [term('ClMelt', 2)], right: [term('Cl2')], electrons: 2, electronSide: 'right' },
      reason: 'Cl⁻ is oxidised to chlorine gas at the anode.',
    },
    cathodeOptions: [P.Mg, P.H2],
    anodeOptions: [P.Cl2, P.O2],
    faraday: { productLabel: 'magnesium', molarMassGmol: MOLAR_MASS.Mg, electronsPerFormula: 2 },
    cations: ['Mg²⁺'],
    anions: ['Cl⁻'],
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'aqueous-cuso4-inert',
    name: 'Copper(II) sulfate solution, inert electrodes',
    kind: 'aqueous',
    electrodes: 'Inert graphite or platinum electrodes',
    cathode: {
      productId: P.Cu.id,
      half: reduce([term('Cu2')], [term('Cu')], 2),
      reason: 'Cu²⁺ (E° = +0.34 V) is a stronger oxidant than water, so copper plates onto the cathode instead of hydrogen forming.',
    },
    anode: {
      productId: P.O2.id,
      half: OXYGEN_FROM_WATER(),
      reason: 'Sulfate is very hard to oxidise, so water is oxidised to oxygen gas and the solution becomes acidic.',
    },
    cathodeOptions: [P.Cu, P.H2],
    anodeOptions: [P.O2, P.S],
    faraday: { productLabel: 'copper', molarMassGmol: MOLAR_MASS.Cu, electronsPerFormula: 2 },
    cations: ['Cu²⁺', 'H⁺'],
    anions: ['SO₄²⁻', 'OH⁻'],
    note: 'The blue colour fades as Cu²⁺ is removed.',
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'aqueous-cuso4-copper',
    name: 'Copper(II) sulfate solution, copper electrodes',
    kind: 'aqueous',
    electrodes: 'Copper anode and copper cathode (electrorefining)',
    cathode: {
      productId: P.Cu.id,
      half: reduce([term('Cu2')], [term('Cu')], 2),
      reason: 'Cu²⁺ is reduced more readily than water, so copper plates onto the cathode.',
    },
    anode: {
      productId: P.CuDissolves.id,
      half: { left: [term('Cu')], right: [term('Cu2')], electrons: 2, electronSide: 'right' },
      reason: 'A copper anode is oxidised more easily than water, so the anode itself dissolves as Cu²⁺ instead of oxygen forming.',
    },
    cathodeOptions: [P.Cu, P.H2],
    anodeOptions: [P.CuDissolves, P.O2],
    faraday: { productLabel: 'copper', molarMassGmol: MOLAR_MASS.Cu, electronsPerFormula: 2 },
    cations: ['Cu²⁺', 'H⁺'],
    anions: ['SO₄²⁻', 'OH⁻'],
    note: 'The Cu²⁺ concentration stays about the same because copper leaves the anode as fast as it plates on the cathode.',
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'aqueous-agno3-inert',
    name: 'Silver nitrate solution, inert electrodes',
    kind: 'aqueous',
    electrodes: 'Inert platinum electrodes',
    cathode: {
      productId: P.Ag.id,
      half: reduce([term('Agp')], [term('Ag')], 1),
      reason: 'Ag⁺ (E° = +0.80 V) is reduced far more readily than water, so silver deposits on the cathode.',
    },
    anode: {
      productId: P.O2.id,
      half: OXYGEN_FROM_WATER(),
      reason:
        'Nitrogen in NO₃⁻ is already at its highest oxidation number (+5), so nitrate cannot be oxidised. Water is oxidised to oxygen gas instead.',
    },
    cathodeOptions: [P.Ag, P.H2],
    anodeOptions: [P.O2, P.NO3],
    faraday: { productLabel: 'silver', molarMassGmol: MOLAR_MASS.Ag, electronsPerFormula: 1 },
    cations: ['Ag⁺', 'H⁺'],
    anions: ['NO₃⁻', 'OH⁻'],
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'aqueous-dilute-h2so4',
    name: 'Dilute sulfuric acid, inert electrodes',
    kind: 'aqueous',
    electrodes: 'Inert platinum electrodes',
    cathode: {
      productId: P.H2.id,
      half: reduce([term('H', 2)], [term('H2')], 2),
      reason: 'H⁺ ions are the only cations, so they are reduced to hydrogen gas at the cathode.',
    },
    anode: {
      productId: P.O2.id,
      half: OXYGEN_FROM_WATER(),
      reason: 'Sulfate is very hard to oxidise, so water is oxidised to oxygen. Overall, water is split in a 2 : 1 volume ratio of H₂ to O₂.',
    },
    cathodeOptions: [P.H2, P.O2],
    anodeOptions: [P.O2, P.S],
    faraday: { productLabel: 'hydrogen gas', molarMassGmol: MOLAR_MASS.H2, electronsPerFormula: 2 },
    cations: ['H⁺'],
    anions: ['SO₄²⁻', 'OH⁻'],
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'aqueous-concentrated-nacl',
    name: 'Concentrated sodium chloride solution (brine), inert electrodes',
    kind: 'aqueous',
    electrodes: 'Inert electrodes',
    cathode: {
      productId: P.H2.id,
      half: reduce([term('H2O', 2)], [term('H2'), term('OH', 2)], 2),
      reason: 'Na⁺ (E° = −2.71 V) is far harder to reduce than water, so water is reduced to hydrogen gas and OH⁻.',
    },
    anode: {
      productId: P.Cl2.id,
      half: { left: [term('Cl', 2)], right: [term('Cl2')], electrons: 2, electronSide: 'right' },
      reason: 'Exception: although E° values alone favour oxygen, oxygen formation has a large overpotential and the Cl⁻ concentration is high, so chlorine forms at the anode.',
    },
    cathodeOptions: [P.H2, P.Na],
    anodeOptions: [P.Cl2, P.O2],
    faraday: { productLabel: 'hydrogen gas', molarMassGmol: MOLAR_MASS.H2, electronsPerFormula: 2 },
    cations: ['Na⁺', 'H⁺'],
    anions: ['Cl⁻', 'OH⁻'],
    note: 'Chlorine over oxygen is a stated exception for concentrated chloride solutions (the chlor-alkali process). Do not apply it to dilute chloride solutions.',
    sourceIds: ['textbook', 'iupacWeights'],
  },
  {
    id: 'aqueous-ki-inert',
    name: 'Potassium iodide solution, inert electrodes',
    kind: 'aqueous',
    electrodes: 'Inert electrodes',
    cathode: {
      productId: P.H2.id,
      half: reduce([term('H2O', 2)], [term('H2'), term('OH', 2)], 2),
      reason: 'K⁺ is far harder to reduce than water, so water is reduced to hydrogen gas and OH⁻.',
    },
    anode: {
      productId: P.I2.id,
      half: { left: [term('I', 2)], right: [term('I2')], electrons: 2, electronSide: 'right' },
      reason: 'I⁻ (E° = +0.54 V for I₂/I⁻) is oxidised more readily than water, so iodine forms and the solution turns brown.',
    },
    cathodeOptions: [P.H2, P.K],
    anodeOptions: [P.I2, P.O2],
    faraday: { productLabel: 'hydrogen gas', molarMassGmol: MOLAR_MASS.H2, electronsPerFormula: 2 },
    cations: ['K⁺', 'H⁺'],
    anions: ['I⁻', 'OH⁻'],
    sourceIds: ['textbook', 'iupacWeights'],
  },
];

/** Electrolytes learners commonly ask about whose products are not decided by this model. */
export const UNSUPPORTED_ELECTROLYTES: { id: string; name: string; reason: string }[] = [
  {
    id: 'aqueous-dilute-nacl',
    name: 'Dilute sodium chloride solution',
    reason: 'The anode product shifts between oxygen and chlorine with concentration, so this model does not predict it.',
  },
  {
    id: 'aqueous-znso4',
    name: 'Zinc sulfate solution',
    reason: 'Zinc or hydrogen can form at the cathode depending on current density and electrode overpotential, so this model does not predict it.',
  },
];

export const FARADAY_LIMITS = {
  minCurrentA: 0.1,
  maxCurrentA: 20,
  minTimeMin: 1,
  maxTimeMin: 600,
} as const;
